import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Ban,
  Briefcase,
  CalendarClock,
  CalendarX,
  CircleCheck,
  CircleDollarSign,
  CircleX,
  Clock,
  CreditCard,
  ExternalLink,
  FileText,
  Hourglass,
  ListTodo,
  Loader2,
  Mail,
  Pencil,
  Receipt,
  Smartphone,
  StickyNote,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import type { AssistantAction, PreviewField, PreviewKind } from "./types";

const KIND_ICON: Record<PreviewKind, React.ElementType> = {
  sms: Smartphone,
  email: Mail,
  task: ListTodo,
  event: CalendarClock,
  event_cancel: CalendarX,
  time_entry: Clock,
  invoice: Receipt,
  payment: CreditCard,
  client: User,
  matter: Briefcase,
  lead: UserPlus,
  note: StickyNote,
  generic: FileText,
};

export function formatMoney(cents?: number, currency?: string) {
  if (typeof cents !== "number" || Number.isNaN(cents)) return "";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      currencyDisplay: "narrowSymbol",
    }).format(cents / 100);
  } catch {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

const findField = (fields: PreviewField[], keys: string[]) =>
  fields.find((f) => keys.includes(f.key)) ||
  fields.find((f) => keys.some((k) => f.key.toLowerCase().includes(k.toLowerCase())));

const SMS_BODY_KEYS = ["body", "message", "text", "content"];
const SMS_FROM_KEYS = ["from", "fromNumber", "from_number"];
const EMAIL_SUBJECT_KEYS = ["subject"];
const EMAIL_BODY_KEYS = ["body", "bodyText", "text", "message", "content", "html"];

function fieldIcon(key: string): React.ElementType | null {
  const k = key.toLowerCase();
  if (/(date|start|end|when|due|time)/.test(k) && !/(duration|minutes|hours)/.test(k)) return CalendarClock;
  if (/(duration|minutes|hours)/.test(k)) return Clock;
  if (/(attendee|assignee|participant|team|recipient)/.test(k)) return Users;
  if (/matter/.test(k)) return Briefcase;
  if (/(client|contact|user|owner)/.test(k)) return User;
  if (/(amount|rate|total|price)/.test(k)) return CircleDollarSign;
  return null;
}

interface Props {
  action: AssistantAction;
  busy?: boolean;
  disabled?: boolean;
  onApprove: (edits?: Record<string, string>) => void;
  onSkip: () => void;
}

const ActionCard: React.FC<Props> = ({ action, busy, disabled, onApprove, onSkip }) => {
  const navigate = useNavigate();
  const p = action.preview || ({ kind: "generic", title: action.tool, summary: "", fields: [] } as any);
  const fields: PreviewField[] = Array.isArray(p.fields) ? p.fields : [];
  const Icon = KIND_ICON[p.kind as PreviewKind] || FileText;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const hasEditable = fields.some((f) => f.editable);
  const status = action.status;
  const isPending = status === "pending";

  const val = (f?: PreviewField) => (f ? (f.key in draft ? draft[f.key] : f.value) : "");

  const startEdit = () => {
    const init: Record<string, string> = {};
    fields.filter((f) => f.editable).forEach((f) => (init[f.key] = f.value ?? ""));
    setDraft(init);
    setEditing(true);
  };

  const changedEdits = () => {
    const out: Record<string, string> = {};
    fields.forEach((f) => {
      if (f.editable && f.key in draft && draft[f.key] !== (f.value ?? "")) out[f.key] = draft[f.key];
    });
    return out;
  };

  // Fields that get a dedicated visual treatment per kind are not repeated in the list
  const featured = (() => {
    const used = new Set<string>();
    let smsBody: PreviewField | undefined;
    let smsFrom: PreviewField | undefined;
    let emailSubject: PreviewField | undefined;
    let emailBody: PreviewField | undefined;
    if (p.kind === "sms") {
      smsBody = findField(fields, SMS_BODY_KEYS) || fields.find((f) => f.multiline);
      smsFrom = fields.find((f) => SMS_FROM_KEYS.includes(f.key));
      if (smsBody) used.add(smsBody.key);
      if (smsFrom) used.add(smsFrom.key);
    }
    if (p.kind === "email") {
      emailSubject = findField(fields, EMAIL_SUBJECT_KEYS);
      emailBody =
        fields.find((f) => EMAIL_BODY_KEYS.includes(f.key)) || fields.find((f) => f.multiline);
      if (emailSubject) used.add(emailSubject.key);
      if (emailBody) used.add(emailBody.key);
    }
    return { used, smsBody, smsFrom, emailSubject, emailBody };
  })();

  const restFields = fields.filter((f) => !featured.used.has(f.key) && !/^(to|recipients?)$/i.test(f.key));
  const recipients = p.recipients || [];
  const warnings = p.warnings || [];
  const link = action.result?.link || p.link;
  const total =
    typeof p.amountCents === "number"
      ? p.amountCents
      : p.lineItems?.length
      ? p.lineItems.reduce((s: number, li: any) => s + (li.amountCents || 0), 0)
      : undefined;

  const renderEditable = (f: PreviewField, opts?: { rows?: number }) =>
    f.multiline || (f.value || "").length > 60 ? (
      <textarea
        aria-label={f.label}
        value={val(f)}
        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
        rows={opts?.rows ?? 4}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-600 focus:outline-none"
      />
    ) : (
      <input
        aria-label={f.label}
        value={val(f)}
        onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-600 focus:outline-none"
      />
    );

  const isEditingField = (f?: PreviewField) => !!f && editing && !!f.editable;

  const statusBadge = () => {
    switch (status) {
      case "executed":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
            <CircleCheck className="h-3 w-3" />
            {action.mode === "auto" ? "Done automatically" : "Done"}
          </span>
        );
      case "skipped":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
            <Ban className="h-3 w-3" />
            Skipped
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">
            <CircleX className="h-3 w-3" />
            Failed
          </span>
        );
      case "expired":
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
            <Hourglass className="h-3 w-3" />
            Expired
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
            Needs approval
          </span>
        );
    }
  };

  return (
    <div
      className={`rounded-xl border bg-white shadow-sm ${
        status === "failed" ? "border-red-200" : isPending ? "border-emerald-200" : "border-slate-200"
      }`}
    >
      {/* Header */}
      <div className="flex items-start gap-3 px-4 pt-3.5">
        <div
          className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
            p.kind === "event_cancel" ? "bg-red-50 text-red-600" : "bg-emerald-50 text-[#0F5132]"
          }`}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-slate-800">{p.title}</p>
            {statusBadge()}
          </div>
          {p.summary && <p className="mt-0.5 text-xs text-slate-500">{p.summary}</p>}
        </div>
      </div>

      <div className="space-y-3 px-4 pb-3.5 pt-3">
        {/* Recipients */}
        {recipients.length > 0 && (
          <div className="text-xs">
            <span className="font-semibold text-slate-500">To </span>
            <span className="text-slate-700">
              {recipients.slice(0, 5).map((r: any, i: number) => (
                <span key={i}>
                  {i > 0 && ", "}
                  <span className="font-medium text-slate-800">{r.name}</span>
                  {r.detail && <span className="text-slate-500"> {r.detail}</span>}
                </span>
              ))}
              {recipients.length > 5 && (
                <span className="text-slate-500"> and {recipients.length - 5} more</span>
              )}
            </span>
          </div>
        )}

        {/* SMS: phone-style bubble */}
        {p.kind === "sms" && featured.smsBody && (
          <div className="rounded-xl bg-slate-50 p-3">
            {featured.smsFrom && (
              <p className="mb-2 text-[11px] text-slate-500">From {featured.smsFrom.value}</p>
            )}
            {isEditingField(featured.smsBody) ? (
              renderEditable({ ...featured.smsBody, multiline: true })
            ) : (
              <div className="ml-auto w-fit max-w-[90%] whitespace-pre-wrap rounded-2xl rounded-br-md bg-[#198754] px-3.5 py-2 text-sm text-white">
                {val(featured.smsBody)}
              </div>
            )}
          </div>
        )}

        {/* Email */}
        {p.kind === "email" && (featured.emailSubject || featured.emailBody) && (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            {featured.emailSubject && (
              <div className="border-b border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Subject</p>
                {isEditingField(featured.emailSubject) ? (
                  <div className="mt-1">{renderEditable({ ...featured.emailSubject, multiline: false })}</div>
                ) : (
                  <p className="text-sm font-medium text-slate-800">{val(featured.emailSubject)}</p>
                )}
              </div>
            )}
            {featured.emailBody && (
              <div className="px-3 py-2">
                {isEditingField(featured.emailBody) ? (
                  renderEditable({ ...featured.emailBody, multiline: true }, { rows: 7 })
                ) : (
                  <p className="max-h-56 overflow-y-auto whitespace-pre-wrap text-sm text-slate-700">
                    {val(featured.emailBody)}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Fields */}
        {restFields.length > 0 && (
          <dl className="space-y-2">
            {restFields.map((f) => {
              const FIcon = fieldIcon(f.key);
              const isBillable = /billable/i.test(f.key);
              return (
                <div key={f.key} className="text-sm">
                  <dt className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    {FIcon && <FIcon className="h-3 w-3" />}
                    {f.label}
                  </dt>
                  <dd className="mt-0.5">
                    {isEditingField(f) ? (
                      renderEditable(f)
                    ) : isBillable ? (
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                          /^(yes|true|billable)$/i.test(f.value)
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {/^(true)$/i.test(f.value) ? "Billable" : /^(false)$/i.test(f.value) ? "Non-billable" : f.value}
                      </span>
                    ) : (
                      <span className="whitespace-pre-wrap text-slate-800">{val(f) || "None"}</span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}

        {/* Line items and totals */}
        {(p.lineItems?.length || typeof total === "number") && (
          <div className="rounded-xl border border-slate-200">
            {p.lineItems?.map((li: any, i: number) => (
              <div
                key={i}
                className="flex items-start justify-between gap-3 border-b border-slate-100 px-3 py-2 text-sm last:border-b-0"
              >
                <span className="min-w-0 flex-1 text-slate-700">{li.description}</span>
                <span className="shrink-0 font-medium tabular-nums text-slate-800">
                  {formatMoney(li.amountCents, p.currency)}
                </span>
              </div>
            ))}
            {typeof total === "number" && (
              <div className="flex items-center justify-between rounded-b-xl bg-slate-50 px-3 py-2 text-sm">
                <span className="font-semibold text-slate-600">
                  {p.kind === "payment" ? "Amount" : "Total"}
                </span>
                <span className="font-bold tabular-nums text-slate-900">
                  {formatMoney(total, p.currency)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <div className="space-y-1.5">
            {warnings.map((w: string, i: number) => (
              <div
                key={i}
                className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{w}</span>
              </div>
            ))}
          </div>
        )}

        {/* Result / error */}
        {status === "executed" && action.result?.summary && (
          <p className="flex items-start gap-2 text-xs text-emerald-700">
            <CircleCheck className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {action.result.summary}
          </p>
        )}
        {status === "failed" && (
          <p className="flex items-start gap-2 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700">
            <CircleX className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {action.error || action.result?.summary || "This action could not be completed."}
          </p>
        )}
        {status === "expired" && (
          <p className="text-xs text-slate-500">
            This request expired before it was approved. Ask again if you still need it.
          </p>
        )}
        {status === "executed" && link?.href && (
          <button
            type="button"
            onClick={() => navigate(link.href)}
            className="inline-flex min-h-[44px] items-center gap-1.5 text-xs font-semibold text-[#0F5132] hover:text-[#146C43] sm:min-h-0"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {link.label || "Open"}
          </button>
        )}

        {/* Buttons */}
        {isPending && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              disabled={busy || disabled}
              onClick={() => {
                const edits = editing ? changedEdits() : undefined;
                onApprove(edits && Object.keys(edits).length ? edits : undefined);
              }}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl bg-[#0F5132] px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#146C43] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-[36px]"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CircleCheck className="h-4 w-4" />}
              {editing ? "Approve with edits" : "Approve"}
            </button>
            {hasEditable && !editing && (
              <button
                type="button"
                disabled={busy || disabled}
                onClick={startEdit}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 sm:min-h-[36px]"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
            )}
            {editing && (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  setDraft({});
                }}
                className="inline-flex min-h-[44px] items-center rounded-xl px-3 py-2 text-sm font-semibold text-slate-500 hover:text-slate-700 sm:min-h-[36px]"
              >
                Cancel edits
              </button>
            )}
            <button
              type="button"
              disabled={busy || disabled}
              onClick={onSkip}
              className="ml-auto inline-flex min-h-[44px] items-center rounded-xl px-3 py-2 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 disabled:opacity-50 sm:min-h-[36px]"
            >
              Skip
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionCard;
